<template>
  <page content-class="admin-campaign-list">
    <campaign-editor :campaign="campaign" step="engagement">

      <div class="d-flex justify-content-center align-items-middle my-5" v-if="!campaign">
        <b-spinner variant="primary"/>
      </div>

      <b-row v-else class="stages-editor m-0">

        <!-- LEFT SIDE -->
        <b-col md="3" lg="2" class="border-right p-0">
          <div class="d-flex flex-column justify-content-between h-100">
            <div class="d-flex justify-content-center p-3 border-bottom">
              <span>Start</span>
            </div>
            <div class="d-flex flex-column justify-content-start align-items-center flex-fill py-2 px-2">
              <div class="border vertical-line"></div>
              <b-card
                v-for="component in orderedComponents"
                :key="component.id"
                class="w-100 mb-2 cursor-pointer"
                @click="changeComponent(component)"
                :style="{
                  background: activeComponent && component.id === activeComponent.id ? (branding ? branding.colour : '#5f27cd') : '#fff',
                  color: activeComponent && component.id === activeComponent.id ? '#fff !important' : 'inherit',
                }"
              >
                <div class="d-flex justify-content-start align-items-center">
                  <div class="pr-2" :style="{width: '36px', fontSize: '24px' }">
                    <font-awesome-icon :icon="componentInfo(component).icon" />
                  </div>
                  <div>
                    <span>{{ componentInfo(component).label }}</span>
                    <template v-if="['email', 'slack', 'teams'].includes(componentInfo(component).key)">
                      <br><span style="font-size: 13px" :class="{ 'text-muted': !(activeComponent && component.id === activeComponent.id) }">
                        <template v-if="componentInfo(component).startDate">
                          Send date: {{componentInfo(component).startDate | formatDate('Do MMM, HH:mm')}}
                        </template>
                        <template v-else>Send: -</template>
                      </span>
                    </template>
                    <template v-else>
                      <br><span style="font-size: 13px" :class="{ 'text-muted': !(activeComponent && component.id === activeComponent.id) }">
                        <template v-if="componentInfo(component).startDate">
                          From: {{componentInfo(component).startDate | formatDate('Do MMM, HH:mm')}}
                        </template>
                        <template v-else>From: -</template>
                      </span>
                      <br><span style="font-size: 13px" :class="{ 'text-muted': !(activeComponent && component.id === activeComponent.id) }">
                        <template v-if="componentInfo(component).endDate">
                          To: {{componentInfo(component).endDate | formatDate('Do MMM, HH:mm')}}
                        </template>
                        <template v-else>To: -</template>
                      </span>
                    </template>
                    <br><b-link @click.prevent="deleteComponent($event, component)" :class="{
                      'text-danger': !(activeComponent && component.id === activeComponent.id),
                      'text-white': activeComponent && component.id === activeComponent.id
                    }" v-if="!componentInfo(component).isScheduled" size="sm">Delete</b-link>
                  </div>
                </div>
              </b-card>
              <span
                class="plus-button"
                :style="{ background: branding ? branding.colour : '#5f27cd'}"
                @click="activeComponent = null"
              >
                <font-awesome-icon icon="plus"/>
              </span>
            </div>
            <div class="d-flex justify-content-center p-3 border-top">
              <span>End</span>
            </div>
          </div>
        </b-col>

        <b-col md="9" lg="10" class="p-0 d-flex flex-column justify-content-between">
          <div>
            <b-row class="m-0 pb-4 pr-4">

              <!-- COMPONENT -->
              <template v-if="activeComponent === null">
                <b-col class="pt-4 pl-4" cols="12">
                  <h2>Engagement</h2>
                  <p>Schedule an action to increase engagement.</p>
                </b-col>
                <b-col v-for="({ key, label, icon, displayOptions, options }, i) in allowedComponents" :key="key" cols="12" class="pb-0 pr-0 pl-4 pt-3">
                  <b-card no-body class="mb-1">
                    <b-card-header class="d-flex justify-content-between align-items-center">
                      <div @click="displayOptions = !displayOptions" v-b-toggle="`component-${key}`">
                        <h4><strong><span class="mr-3 text-muted"><font-awesome-icon :icon="icon" /></span>{{ label }}</strong></h4>
                      </div>
                      <b-button @click="allowedComponents[i].displayOptions = !displayOptions"  v-b-toggle="`component-${key}`" variant="outline-primary" class="px-2" size="sm">
                        <font-awesome-icon v-if="displayOptions" icon="chevron-up" size="sm"/>
                        <font-awesome-icon v-else icon="chevron-down" size="sm"/>
                      </b-button>
                    </b-card-header>
                    <b-collapse :id="`component-${key}`" :accordion="`component-${key}`" role="tabpanel">
                      <div v-for="{ id, title, body, description, type } in options" :key="id" class="border-bottom d-flex justify-content-between align-items-center p-3">
                        <div>
                          <span style="font-size: 15px"><strong>{{ title }}</strong> ({{ type }})</span><br>
                          <span class="text-muted">{{ description }}</span>
                        </div>
                        <div style="width: 160px" class="d-flex justify-content-end align-items-center">
                          <b-button size="sm" variant="primary" @click="createComponent(key, id)">Select</b-button>
                        </div>
                        <b-modal v-if="body" :id="`preview-modal-${id}`" :title="`${title} - Preview`" ok-only ok-title="Select" @ok="createComponent(key, id)">
                          <div v-html="body"></div>
                        </b-modal>
                      </div>
                    </b-collapse>
                  </b-card>
                </b-col>
              </template>
              <template v-if="activeComponent === 'loading'">
                <b-col md="12" class="p-5 d-flex justify-content-center align-items-middle">
                  <b-spinner variant="primary" label="Loading..."></b-spinner>
                </b-col>
              </template>
              <template v-else-if="componentInfo(activeComponent).key === 'email'">
                <b-col md="12" class="p-4">
                  <update-email-message
                    :campaignID="campaignID"
                    :collectiveGoal="collectiveGoal"
                    :value="activeComponent"
                    :syncedEmailAddresses="syncedEmailAccounts"
                    @input="updateComponent($event)"
                  />
                </b-col>
              </template>
              <template v-else-if="componentInfo(activeComponent).key === 'slack'">
                <b-col md="12" class="p-4">
                  <update-slack-message
                    :campaignID="campaignID"
                    :collectiveGoal="collectiveGoal"
                    :value="activeComponent"
                    :syncedEmailAddresses="(syncedEmailAddresses || {}).syncedSlackEmails"
                    @input="updateComponent($event)"
                  />
                </b-col>
              </template>
              <template v-else-if="componentInfo(activeComponent).key === 'teams'">
                <b-col md="12" class="p-4">
                  <update-teams-message
                    :campaignID="campaignID"
                    :collectiveGoal="collectiveGoal"
                    :value="activeComponent"
                    :syncedEmailAddresses="(syncedEmailAddresses || {}).syncedMicrosoftEmails"
                    @input="updateComponent($event)"
                  />
                </b-col>
              </template>
              <template v-else-if="componentInfo(activeComponent).key === 'referral-jam'">
                <b-col md="12" class="pl-3 pr-2 py-4">
                  <update-referral-jam :eventID="activeComponent.event.id" :campaignID="campaignID" :jobs="campaign.jobs" />
                </b-col>
              </template>
            </b-row>
          </div>
          <div class="d-flex justify-content-end align-items-center px-3 py-2 border-top">
            <!-- b-button @click="console(form)">Console</b-button -->
            <b-button
              class="px-3"
              variant="outline-primary"
              @click="$router.push({ name: 'EditCampaignParticipants', params: { campaignID: campaign.id } })"
            >Next</b-button>
          </div>
        </b-col>
      </b-row>
    </campaign-editor>
  </page>
</template>

<script>
import gql from 'graphql-tag';
import { Twitter as ColourPicker } from 'vue-color';
import emailTemplates from './templates/emails';
import slackTemplates from './templates/slack';
import teamsTemplates from './templates/teams';
import CampaignAdminStagesQuery from '@/graphql/queries/CampaignAdminStages.gql';
import CreateCampaignComponentMutation from '@/graphql/mutations/CreateCampaignComponent.gql';
import UpdateCampaignComponentMutation from '@/graphql/mutations/UpdateCampaignComponent.gql';
import Page from '@/components/campaign-admin/page';
import formatDate from 'date-fns/format';
import CampaignEditor from '@/components/campaign-admin/campaign-editor-frame';
import UpdateEmailMessage from '@/components/campaign-admin/update-email-message';
import UpdateSlackMessage from '@/components/campaign-admin/update-slack-message';
import UpdateTeamsMessage from '@/components/campaign-admin/update-teams-message';
import UpdateReferralJam from '@/components/campaign-admin/update-referral-jam';
import { mapRoot } from '@src/mixins/root';

const CreateEventMutation = gql`
  mutation CreateEvent($create: CreateEventInput!) {
    event: createEvent(create: $create) {
      id
    }
  }
`;

const DeleteCampaignComponentMutation = gql`
  mutation CreateEvent($id: ID!, $campaignID: ID!) {
    deleteCampaignComponent(id: $id, campaign: { id: $campaignID })
  }
`;

const SyncedEmailAddressesQuery = gql`
  query SyncedEmailAddresses($userID: ID!) {
    company {
      id
      syncedSlackEmails: users(
        where: {
          id: $userID,
          NOT: { AND: [ { state: null }, { state: { slack: null } } ] }
        }
      ) {
          id
          emailAddress
          fullName
      }
      syncedMicrosoftEmails: users(
        where: {
          NOT: { AND: [ { state: null }, { state: { microsoft: null } } ] }
        }
      ) {
          id
          emailAddress
          fullName
      }
      syncedGmailEmails: users(
        where: {
          NOT: { AND: [ { state: null }, { state: { gmail: null } } ] }
        }
      ) {
          id
          emailAddress
          fullName
      }
    }
  }
`;

const allowedComponents = templateFolder => ([ {
  key: 'email',
  label: 'Scheduled Emails',
  icon: 'envelope',
  status: 'ACTIVE',
  displayOptions: false,
  options: emailTemplates(templateFolder)
}, {
  key: 'slack',
  label: 'Scheduled Slack Messages',
  icon: [ 'fab', 'slack' ],
  status: 'ACTIVE',
  displayOptions: false,
  options: slackTemplates
}, {
  key: 'teams',
  label: 'Scheduled Teams Messages',
  icon: [ 'fab', 'windows' ],
  status: 'ACTIVE',
  displayOptions: false,
  options: teamsTemplates(templateFolder)
}, {
  key: 'referral-jam',
  label: 'Events',
  icon: 'calendar',
  status: 'ACTIVE',
  displayOptions: false,
  options: [ {
    id: 'event_referral_jam',
    title: 'Referral Jam',
    type: 'Group',
    description: 'A short usually 30 minutes live event to boost referrals',
  } ]
} ]);

export default {
  name: 'CampaignAdminStages',
  components: {
    Page,
    CampaignEditor,
    ColourPicker,
    UpdateReferralJam,
    UpdateEmailMessage,
    UpdateSlackMessage,
    UpdateTeamsMessage
  },
  props: {
    campaignID: {
      type: String,
    },
  },
  data: () => ({
    activeComponent: null
  }),
  apollo: {
    campaign: {
      query: CampaignAdminStagesQuery,
      variables() {
        const { campaignID } = this;
        return { campaignID, withJobs: true };
      },
      update({ company }) {
        const { campaign } = company;
        return campaign;
      },
      error(err) {
        this.$notifyFatalErr(err, { prepend: 'Failed to load the campaigns' });
      },
      errorPolicy: 'all',
      fetchPolicy: 'network-only',
    },
    syncedEmailAddresses: {
      query: SyncedEmailAddressesQuery,
      variables() {
        const { user } = this;
        const { id: userID } = user || {};
        return { userID };
      },
      update({ company }) {
        const { syncedSlackEmails, syncedMicrosoftEmails, syncedGmailEmails } = company || {};
        return { syncedSlackEmails, syncedMicrosoftEmails, syncedGmailEmails };
      },
      error(err) {
        this.$notifyFatalErr(err, { prepend: 'Failed to load the synced email addresses' });
      },
      errorPolicy: 'all',
      fetchPolicy: 'network-only',
    },
  },
  computed: {
    ...mapRoot([ 'user', 'isDemoSite', 'isInMobi', 'isLNW' ]),
    allowedComponents() {
      const { isInMobi, isLNW } = this;

      let templateFolder = null;
      if (isInMobi) {
        templateFolder = 'inMobi';
      }
      if (isLNW) {
        templateFolder = 'lnw';
      }

      return allowedComponents(templateFolder);
    },
    formatDate: () => formatDate,
    components() {
      const { campaign } = this;
      if (!campaign) return [];
      return campaign.components;
    },
    orderedComponents() {
      const { components } = this;
      const orderedComponents = (components || [])
        .filter(({ slackMessage, teamsMessage, emailMessage, event }) => slackMessage || teamsMessage || emailMessage || event)
        .sort((a, b) => {
          const { slackMessage: aSlackMessage, teamsMessage: aTeamsMessage, emailMessage: aEmailMessage, event: aEvent } = a;
          const { slackMessage: bSlackMessage, teamsMessage: bTeamsMessage, emailMessage: bEmailMessage, event: bEvent } = b;
          const { sendAt: aSendAt, startDate: aStartDate } = { ...(aSlackMessage || {}), ...(aTeamsMessage || {}), ...(aEmailMessage || {}), ...(aEvent || {}) };
          const { sendAt: bSendAt, startDate: bStartDate } = { ...(bSlackMessage || {}), ...(bTeamsMessage || {}), ...(bEmailMessage || {}), ...(bEvent || {}) };
          const aDate = (aSendAt || aStartDate || 0);
          const bDate = (bSendAt || bStartDate || 0);
          console.log({ aSlackMessage, aTeamsMessage, aEmailMessage, aEvent });
          console.log({ aDate, bDate });
          return (aSendAt || aStartDate || 0) - (bSendAt || bStartDate || 0);
        });
      return orderedComponents;
    },
    branding() {
      const { campaign } = this;
      if (!campaign) return {};
      return campaign.branding;
    },
    collectiveGoal() {
      const { components } = this;
      if (!components) return null;
      const collectiveGoal = components.find(({ collectiveGoal }) => collectiveGoal);
      return collectiveGoal ? collectiveGoal.collectiveGoal : null;
    },
    syncedEmailAccounts() {
      const { syncedEmailAddresses } = this;
      const { syncedMicrosoftEmails, syncedGmailEmails } = syncedEmailAddresses || {};
      if (Array.isArray(syncedMicrosoftEmails) && syncedMicrosoftEmails.length && Array.isArray(syncedGmailEmails) && syncedGmailEmails.length) {
        const mergedEmails = [ ...syncedMicrosoftEmails, ...syncedGmailEmails ];
        const uniqueIds = [];
        return mergedEmails.filter(({ id }) => {
          if (!uniqueIds.includes(id)) {
            uniqueIds.push(id);
            return true;
          }
          return false;
        });
      }
      if (Array.isArray(syncedMicrosoftEmails) && syncedMicrosoftEmails.length) {
        return syncedMicrosoftEmails;
      }
      if (Array.isArray(syncedGmailEmails) && syncedGmailEmails.length) {
        return syncedGmailEmails;
      }
      return null;
    }
  },
  methods: {
    changeComponent(component) {
      this.activeComponent = 'loading';
      setTimeout(() => {
        this.activeComponent = component;
      }, 500);
    },
    async createComponent(key, optionID) {
      const component = this.allowedComponents.find(({ key: k }) => k === key);
      if (!component) return;
      const { campaignID: id, isInMobi, isLNW } = this;

      let templateFolder = null;
      if (isInMobi) {
        templateFolder = 'inMobi';
      }
      if (isLNW) {
        templateFolder = 'lnw';
      }

      const create = {};
      if (key === 'email') {
        const { body, title, subject, inputs, id } = emailTemplates(templateFolder).find(({ id }) => id === optionID);
        create.emailMessage = {
          from: 'NONE',
          to: 'ALL_USERS',
          status: 'DRAFT',
          body: typeof body === 'string' ? body : (body ? JSON.stringify(body) : null),
          title,
          subject: subject || null,
          template: id,
          inputs: Object.keys(inputs || {}).reduce((r, key) => ({ ...r, [key]: inputs[key].defaultValue || null }), {}),
        };
      } else if (key === 'slack') {
        const { body, title, inputs, id } = slackTemplates.find(({ id }) => id === optionID);
        create.slackMessage = {
          from: 'NONE',
          to: 'ALL_USERS',
          status: 'DRAFT',
          body: typeof body === 'string' ? body : (body ? JSON.stringify(body) : null),
          title,
          template: id,
          inputs: Object.keys(inputs || {}).reduce((r, key) => ({ ...r, [key]: inputs[key].defaultValue || null }), {}),
        };
      } else if (key === 'teams') {
        const { body, title, inputs, id } = teamsTemplates(templateFolder).find(({ id }) => id === optionID);
        create.teamsMessage = {
          from: 'NONE',
          to: 'ALL_USERS',
          status: 'DRAFT',
          body: typeof body === 'string' ? body : (body ? JSON.stringify(body) : null),
          title,
          template: id,
          inputs: Object.keys(inputs || {}).reduce((r, key) => ({ ...r, [key]: inputs[key].defaultValue || null }), {}),
        };
      } else if (key === 'referral-jam') {
        const { components } = this;
        const { collectiveGoal, leaderboard } = (components || []).reduce((acc, { collectiveGoal, leaderboard }) => {
          if (collectiveGoal) acc.collectiveGoal = JSON.parse(JSON.stringify(collectiveGoal));
          if (leaderboard) {
            const { type, startDate, endDate, ...rest } = leaderboard;
            acc.leaderboard = JSON.parse(JSON.stringify(rest));
          }
          return acc;
        }, { collectiveGoal: null, leaderboard: null });
        if (collectiveGoal) delete this.removeTypeField(collectiveGoal);
        if (leaderboard) delete this.removeTypeField(leaderboard);

        const { data, errors } = await this.$apollo.mutate({
          mutation: CreateEventMutation,
          variables: {
            create: {
              title: 'Referral Jam',
              collectiveGoal,
              leaderboard,
              preferences: {
                canScheduleLinkedIn: true
              }
            },
          },
        });
        if (Array.isArray(errors)) {
          errors.forEach(err => this.$log.error('createEvent', err));
          throw errors.shift();
        }
        this.$assert(data && data.event && data.event.id, 404, 'Failed to create new event');
        const { event } = data;
        const { id: eventID } = event || {};
        create.event = {
          id: eventID,
        };
      }
      console.log({ create });
      try {
        const { campaignID } = this;
        await this.$apollo.mutate({
          mutation: CreateCampaignComponentMutation,
          variables: {
            campaign: { id },
            create,
          },
          update(store, { data: { createCampaignComponent } }) {
            const { company } = store.readQuery({
              query: CampaignAdminStagesQuery,
              variables: { campaignID }
            });
            if (company.campaign && Array.isArray(company.campaign.components)) {
              company.campaign.components.push(createCampaignComponent);
            } else {
              company.campaign.components = [ createCampaignComponent ];
            }
            company.campaign.components.map(el => {
              if (el.emailMessage) {
                el.emailMessage.__typename = 'EmailMessage';
              }
              if (el.slackMessage) {
                el.slackMessage.__typename = 'SlackMessage';
              }
              return el;
            });
            store.writeQuery({
              query: CampaignAdminStagesQuery,
              variables: { campaignID },
              data: {
                company
              }
            });
          },
        });
        this.$notification({ message: 'Component is successfully added', variant: 'success' });
      } catch (error) {
        this.$notifyErr(error, { prepend: 'Failed to save component' });
        console.log(error);
      }
    },
    async updateComponent(update) {
      console.log({ update });
      const { campaignID: id, activeComponent } = this;
      try {
        await this.$apollo.mutate({
          mutation: UpdateCampaignComponentMutation,
          variables: {
            campaign: { id },
            componentID: activeComponent.id,
            update,
          },
        });
        this.$notification({ message: 'Component is successfully updated', variant: 'success' });
      } catch (error) {
        this.$notifyErr(error, { prepend: 'Failed to update component' });
        console.log(error);
      }
    },
    async deleteComponent(event, component) {
      try {
        event.stopPropagation();
        const { id } = component;
        const { campaignID } = this;
        this.activeComponent = null;
        await this.$apollo.mutate({
          mutation: DeleteCampaignComponentMutation,
          variables: { id, campaignID },
          update(store) {
            const { company } = store.readQuery({
              query: CampaignAdminStagesQuery,
              variables: { campaignID }
            });

            if (company.campaign && Array.isArray(company.campaign.components)) {
              const index = company.campaign.components.findIndex(({ id: componentID }) => id === componentID);
              company.campaign.components.splice(index, 1);
            }
            store.writeQuery({
              query: CampaignAdminStagesQuery,
              variables: { campaignID: id },
              data: {
                company
              }
            });
          },
        });
      } catch (error) {
        console.log(error);
        this.$notifyErr(error, { prepend: error.message || 'Failed to delete component' });
      }
    },
    componentInfo(component) {
      if (!component || typeof component === 'string') return {};
      const { event, emailMessage, slackMessage, teamsMessage } = component;
      const { parseDate } = this;
      const { status, sendAt } = emailMessage || slackMessage || teamsMessage || {};
      const isScheduledByStatus = [ 'SCHEDULED', 'SCHEDULING', 'ONHOLD', 'DELIVERED', 'ERRORED' ].includes(status);
      const now = new Date();
      const sendAtDate = typeof sendAt === 'string' || typeof sendAt === 'number' ? new Date(sendAt) : sendAt;
      const isLessThanFiveMinutes = sendAtDate && (sendAtDate.getTime() - now.getTime()) < 300000 && status === 'READY';
      const isScheduled = isScheduledByStatus || isLessThanFiveMinutes;
      if (event) {
        const { key, label, icon } = this.allowedComponents.find(({ key: k }) => k === 'referral-jam');
        const { startDate, endDate } = event;
        return { key, label: event.title ? event.title : label, icon, startDate: parseDate(startDate), endDate: parseDate(endDate) };
      } else if (emailMessage) {
        const { key, label, icon } = this.allowedComponents.find(({ key: k }) => k === 'email');
        const { sendAt } = emailMessage;
        return { key, label: emailMessage.title ? emailMessage.title : label, icon, startDate: parseDate(sendAt), isScheduled };
      } else if (slackMessage) {
        const { key, label, icon } = this.allowedComponents.find(({ key: k }) => k === 'slack');
        const { sendAt } = slackMessage;
        return { key, label: slackMessage.title ? slackMessage.title : label, icon, startDate: parseDate(sendAt), isScheduled };
      } else if (teamsMessage) {
        const { key, label, icon } = this.allowedComponents.find(({ key: k }) => k === 'teams');
        const { sendAt } = teamsMessage;
        return { key, label: teamsMessage.title ? teamsMessage.title : label, icon, startDate: parseDate(sendAt), isScheduled };
      }
      return {};
    },
    parseDate(dateString) {
      if (!dateString) return null;
      let date = Date.parse(dateString);
      if (isNaN(date)) {
        date = new Date(parseInt(dateString));
      }
      if (isNaN(date)) {
        date = new Date(dateString);
      }
      if (!isNaN(date)) {
        return date.getTime();
      }
      return null;
    },
    removeTypeField(obj) {
      const { removeTypeField } = this;
      if (!obj || typeof obj !== 'object') {
        return;
      }
      if (Array.isArray(obj)) {
        for (let i = 0; i < obj.length; i++) {
          removeTypeField(obj[i]);
        }
      } else {
        for (const prop in obj) {
          if (prop === '__typename' || prop === 'fileName' || prop === 'fileLocation' || prop === 'fileSize') {
            delete obj[prop];
          } else {
            removeTypeField(obj[prop]);
          }
        }
      }
    }
  },
};
</script>

<style lang="scss">
.stages-editor {
  .vertical-line {
    width: 0px;
    position: absolute;
    top: 54px;
    bottom: 54px;
    z-index: 0;
  }
  .plus-button {
    display: block;
    width: 36px;
    line-height: 36px;
    text-align: center;
    color: #fff;
    border-radius: 18px;
    z-index: 1;
    cursor: pointer;

    &:hover {
      opacity: 0.9;
    }

    &:active {
      opacity: 0.95;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/views/campaign-admin/Engagement.vue